import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Box, Alert, Stack } from '@mui/material';
import DataTable from './DataTable';
import { Chain } from '../services/chainService';
import { Store, TwoGISStoreResponse } from '../services/storeService';

interface ChainFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (chain: Partial<Chain>) => void;
  initialValues?: Partial<Chain>;
  stores: Store[];
  loading?: boolean;
  error?: string | null;
  onFetch2GIS: (chainName: string, town: string, addToDb?: boolean) => Promise<TwoGISStoreResponse>;
}

const ChainFormDialog: React.FC<ChainFormDialogProps> = ({
  open,
  onClose,
  onSubmit,
  initialValues = {},
  stores,
  loading = false,
  // _error = null,
  onFetch2GIS,
}) => {
  const [name, setName] = useState(initialValues.name || '');
  const [contactInfo, setContactInfo] = useState(initialValues.contact_info || '');
  const [description, setDescription] = useState(initialValues.description || '');
  const [town, setTown] = useState('');
  const [addResult, setAddResult] = useState<{ added: number; skipped: number; found: number } | null>(null);
  const [addError, setAddError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [fetching, _] = useState(false);

  React.useEffect(() => {
    setName(initialValues.name || '');
    setContactInfo(initialValues.contact_info || '');
    setDescription(initialValues.description || '');
  }, [initialValues, open]);

  const filteredStores = stores.filter(s => s.chain_name === name);

  const columns = [
    { field: 'address', headerName: 'Address', flex: 2 },
    { field: 'lat', headerName: 'Lat', width: 120 },
    { field: 'lng', headerName: 'Lng', width: 120 },
    { field: 'work_schedule', headerName: 'Work Schedule', flex: 1 },
  ];

  const handleAddToDb = async () => {
    setAdding(true);
    setAddError(null);
    setAddResult(null);
    try {
      const res = await onFetch2GIS(name, town, true);
      setAddResult({ added: res.added, skipped: res.skipped, found: res.found });
    } catch (err: any) {
      setAddError(err?.message || 'Failed to add stores to database');
    } finally {
      setAdding(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{initialValues && initialValues.name ? 'Edit Chain' : 'Add Chain'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} pt={1}>
          <TextField
            label="Name"
            value={name}
            onChange={e => setName(e.target.value)}
            fullWidth
          />
          <TextField
            label="Contact Info"
            value={contactInfo}
            onChange={e => setContactInfo(e.target.value)}
            fullWidth
          />
          <TextField
            label="Description"
            value={description}
            onChange={e => setDescription(e.target.value)}
            fullWidth
          />
        </Stack>
        <Box mt={3} mb={2} display="flex" gap={2} alignItems="center">
          <TextField
            label="Town"
            value={town}
            onChange={e => setTown(e.target.value)}
            size="small"
          />
          <Button
            variant="contained"
            onClick={handleAddToDb}
            disabled={!town || adding}
          >
            Fetch & Add Stores from 2GIS
          </Button>
        </Box>
        {/*{_error && <Alert severity="error">{error}</Alert>}*/}
        {addError && <Alert severity="error">{addError}</Alert>}
        {addResult && (
          <Alert severity="success">
            Added: {addResult.added}, Skipped: {addResult.skipped}, Found: {addResult.found}
          </Alert>
        )}
        <DataTable rows={filteredStores.map((s, i) => ({ ...s, id: s.id || i }))} columns={columns} loading={loading || adding || fetching} pageSize={10} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={() => onSubmit({ name, contact_info: contactInfo, description })}
          variant="contained"
          disabled={loading}
        >
          {initialValues && initialValues.name ? 'Save' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChainFormDialog; 